// @ts-nocheck
// Frigid Well — ritual soak telegraph. Gold/amber rune donut at arena center;
// deliberately NOT frost-blue like Glacial Drift ice patches.
import { TAU, clamp } from "../../util";
import { uiFont } from "../../ui-tokens";
import { Game } from "../game-base";

Game.FRIGID_WELL_FUSE = 5.0;
Game.FRIGID_WELL_WINDUP = 3.0;
Game.FRIGID_WELL_TOTAL = Game.FRIGID_WELL_WINDUP + Game.FRIGID_WELL_FUSE;

const RITUAL_GOLD = "#ffc850";
const RITUAL_AMBER = "#ff9020";

function frigidWellFuseMax(sr) {
  return sr?.fuseMax || Game.FRIGID_WELL_TOTAL;
}

function frigidWellFuseProg(sr) {
  const max = frigidWellFuseMax(sr);
  if (max <= 0) return 0;
  return clamp(1 - (sr.fuse || 0) / max, 0, 1);
}

function drawRuneOctagon(c, r, rot, alpha, dashed) {
  c.beginPath();
  for (let i = 0; i <= 8; i++) {
    const a = rot + (i / 8) * TAU;
    const rr = i % 2 === 0 ? r : r * 0.92;
    const x = Math.cos(a) * rr;
    const y = Math.sin(a) * rr;
    if (i === 0) c.moveTo(x, y);
    else c.lineTo(x, y);
  }
  c.closePath();
  c.strokeStyle = `rgba(255,200,80,${alpha})`;
  c.lineWidth = dashed ? 3 : 5;
  if (dashed) c.setLineDash([10, 7]);
  c.stroke();
  c.setLineDash([]);
}

function drawInwardChevrons(c, innerR, outerR, t, alpha, count) {
  for (let i = 0; i < count; i++) {
    const a = (i / count) * TAU + t * 0.6;
    const mid = (innerR + outerR) * 0.5;
    const cx = Math.cos(a) * mid;
    const cy = Math.sin(a) * mid;
    const toward = Math.atan2(-cy, -cx);
    const sz = (outerR - innerR) * 0.35;
    c.save();
    c.translate(cx, cy);
    c.rotate(toward);
    c.fillStyle = `rgba(255,180,60,${alpha})`;
    c.beginPath();
    c.moveTo(sz, 0);
    c.lineTo(-sz * 0.55, sz * 0.55);
    c.lineTo(-sz * 0.55, -sz * 0.55);
    c.closePath();
    c.fill();
    c.restore();
  }
}

function drawRitualPillars(c, r, t, alpha) {
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * TAU + Math.PI / 4;
    const px = Math.cos(a) * r * 1.02;
    const py = Math.sin(a) * r * 1.02;
    const h = 28 + 10 * Math.sin(t * 8 + i);
    const grad = c.createLinearGradient(px, py - h, px, py + h * 0.3);
    grad.addColorStop(0, `rgba(255,220,120,${alpha * 0.9})`);
    grad.addColorStop(1, "rgba(128,64,160,0)");
    c.fillStyle = grad;
    c.fillRect(px - 3, py - h, 6, h * 1.3);
  }
}

function drawSoakCountdownArc(c, r, innerR, fuseProg, urgent) {
  const bandMid = (r + innerR) * 0.5;
  const bandW = (r - innerR) * 0.85;
  c.lineWidth = bandW;
  c.lineCap = "butt";
  c.strokeStyle = urgent ? "rgba(80,32,96,0.55)" : "rgba(48,24,64,0.45)";
  c.beginPath();
  c.arc(0, 0, bandMid, 0, TAU);
  c.stroke();
  const sweep = fuseProg * TAU - Math.PI / 2;
  c.strokeStyle = urgent ? RITUAL_AMBER : RITUAL_GOLD;
  c.shadowColor = urgent ? RITUAL_AMBER : RITUAL_GOLD;
  c.shadowBlur = urgent ? 18 : 10;
  c.beginPath();
  c.arc(0, 0, bandMid, -Math.PI / 2, sweep);
  c.stroke();
  c.shadowBlur = 0;
}

function drawCenterSoakMark(c, t, urgent, scale) {
  const pulse = 0.5 + 0.5 * Math.sin(t * 7);
  c.save();
  c.scale(scale, scale);
  c.strokeStyle = urgent ? RITUAL_AMBER : RITUAL_GOLD;
  c.lineWidth = urgent ? 5 : 4;
  c.globalAlpha = 0.85 + pulse * 0.15;
  const arm = 22 + pulse * 6;
  c.beginPath();
  c.moveTo(-arm, 0);
  c.lineTo(arm, 0);
  c.moveTo(0, -arm);
  c.lineTo(0, arm);
  c.stroke();
  c.beginPath();
  c.arc(0, 0, 10 + pulse * 4, 0, TAU);
  c.stroke();
  c.fillStyle = urgent ? "rgba(255,144,32,0.35)" : "rgba(255,200,80,0.28)";
  c.beginPath();
  c.arc(0, 0, 8 + pulse * 3, 0, TAU);
  c.fill();
  c.restore();
}

function drawFrigidWellRitual(c, x, y, r, fuseProg, t, urgent, showLabel) {
  c.save();
  c.translate(x, y);

  const innerR = r * 0.62;
  const outerR = r * 1.06;

  c.globalCompositeOperation = "lighter";
  c.fillStyle = `rgba(128,64,160,${0.06 + fuseProg * 0.08})`;
  c.beginPath();
  c.arc(0, 0, outerR, 0, TAU);
  c.arc(0, 0, innerR, 0, TAU, true);
  c.fill();

  drawSoakCountdownArc(c, r, innerR, fuseProg, urgent);
  drawInwardChevrons(c, innerR * 0.95, r * 0.98, t, 0.55 + fuseProg * 0.35, 12);
  drawRitualPillars(c, r, t, 0.5 + fuseProg * 0.3);

  c.globalCompositeOperation = "source-over";
  drawRuneOctagon(c, r * 1.04, t * 0.25, 0.75 + fuseProg * 0.2, false);
  drawRuneOctagon(c, innerR * 0.98, -t * 0.18, 0.45, true);

  c.strokeStyle = urgent ? "rgba(255,144,32,0.95)" : "rgba(255,200,80,0.9)";
  c.lineWidth = urgent ? 7 : 5;
  c.beginPath();
  c.arc(0, 0, r, 0, TAU);
  c.stroke();

  drawCenterSoakMark(c, t, urgent, 1.1);

  if (showLabel) {
    c.font = uiFont("bold", "md");
    c.textAlign = "center";
    c.textBaseline = "middle";
    c.fillStyle = urgent ? "#ffd878" : "#ffe8a0";
    c.strokeStyle = "rgba(32,8,48,0.85)";
    c.lineWidth = 4;
    const labelY = -r * 0.28;
    c.strokeText("SOAK", 0, labelY);
    c.fillText("SOAK", 0, labelY);
    c.font = uiFont("normal", "sm");
    c.fillStyle = urgent ? "#ffb060" : "#ffc850";
    c.strokeText("ALL PLAYERS", 0, labelY + 22);
    c.fillText("ALL PLAYERS", 0, labelY + 22);
  }

  c.restore();
}

Game.prototype.drawSoakRings = function () {
  const c = this.ctx;
  const rings = this.soakRings || [];
  if (!rings.length) return;
  const t = this.nowS || 0;

  for (const sr of rings) {
    const fuseProg = frigidWellFuseProg(sr);
    const urgent = fuseProg > 0.72;
    drawFrigidWellRitual(c, sr.x, sr.y, sr.r, fuseProg, t, urgent, true);
  }
};

Game.prototype.frigidWellProgress = function () {
  if (!this.showBossChannelHud?.()) return null;
  const boss = this.players.find(pl => pl.boss === "vorathal" && pl.alive);
  if (!boss) return null;

  const ring = (this.soakRings || []).find(sr => sr.spellId === "boss_frigid_well" && (sr.fuse || 0) > 0);
  if (!ring) return null;

  const fuseProg = frigidWellFuseProg(ring);
  const winding = boss.channelSpell === "boss_frigid_well";
  return {
    active: true,
    phase: winding ? "windup" : "fuse",
    prog: Math.max(fuseProg, 0.04),
    channelProg: winding ? clamp(boss.channelProg, 0, 1) : 1,
    urgent: fuseProg > 0.72,
  };
};
